import { useCallback, useMemo } from "react";

import { useSearchParams } from "@remix-run/react";

import { useSupabaseSession } from "~/context/SupabaseSessionContext";

interface RequestParams {
    params?: Record<string, string | undefined | null>;
}

interface RequestOptions extends RequestInit {
    params?: Record<string, string | undefined | null>;
}

export const useApiClient = () => {
    const { token: accessToken, loading } = useSupabaseSession();
    const [searchParams] = useSearchParams();
    const sessionToken = useMemo(
        () => searchParams.get("sessionToken"),
        [searchParams],
    );

    const buildUrl = useCallback(
        (
            baseUrl: string,
            params?: Record<string, string | null | undefined>,
        ) => {
            // Check if we're on the client to avoid SSR issues
            const baseUrlPrefix =
                typeof window !== "undefined"
                    ? window.location.protocol +
                      "//" +
                      window.location.host +
                      "/api"
                    : "/api";

            const url = new URL(
                baseUrlPrefix + baseUrl,
                typeof window !== "undefined"
                    ? window.location.origin
                    : "http://localhost:3000",
            );

            // TODO: this feels hacky to include sessionToken whenever we find it in the URL. Hmm...
            const paramsWithSessionToken = {
                ...params,
                sessionToken: sessionToken,
            };
            if (paramsWithSessionToken) {
                for (const [key, value] of Object.entries(
                    paramsWithSessionToken,
                )) {
                    if (value !== undefined && value !== null) {
                        url.searchParams.set(key, value);
                    }
                }
            }
            return url.toString();
        },
        [sessionToken],
    );

    const request = useCallback(
        async (url: string, options: RequestOptions) => {
            const finalUrl = buildUrl(url, options.params);

            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };

            if (accessToken) {
                headers.Authorization = `Bearer ${accessToken}`;
            }

            if (options.headers) {
                Object.assign(headers, options.headers);
            }

            return fetch(finalUrl, {
                ...options,
                headers,
            });
        },
        [accessToken, buildUrl],
    );

    return {
        get: (url: string, options: RequestParams = {}) =>
            request(url, { method: "GET", ...options }),
        post: (
            url: string,
            body?: Record<string, unknown>,
            options: RequestParams = {},
        ) =>
            request(url, {
                method: "POST",
                body: JSON.stringify(body),
                ...options,
            }),
        put: (
            url: string,
            body?: Record<string, unknown>,
            options: RequestParams = {},
        ) =>
            request(url, {
                method: "PUT",
                body: JSON.stringify(body),
                ...options,
            }),
        delete: (url: string, options: RequestParams = {}) =>
            request(url, { method: "DELETE", ...options }),
        loading,
    };
};
